import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  ChevronDownIcon,
  CloseIcon,
  Flex,
  FormControl,
  FormLabel,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuFuseHighlight,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuSearchBox,
  MenuSearchBoxOption,
  Tag,
  TagCloseButton,
  VirtualizedList,
} from '@cdl/heartbeat-react';
import { Tags } from '../../../types/annotation-task-detail.type';

type Props = {
  data: MenuSearchBoxOption[];
  selectedData: Tags;
  handleDataChange: (values: Tags, type?: string, wave?: string) => void;
  type?: string;
  wave?: string;
  preSelected: string[];
  clear?: boolean;
  setIsClear: (value: boolean) => void;
  label: string;
};

export const DropdownSettings: React.FC<Props> = ({
  selectedData,
  data,
  handleDataChange,
  type,
  preSelected,
  wave,
  clear,
  setIsClear,
  label,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(preSelected);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
      setIsClear(false);
    }
  }, [clear, setIsClear]);

  const handleSelectedDataChange = useCallback(
    (optionCodes: string | string[]) => {
      const tags = Array.isArray(optionCodes) ? optionCodes : [optionCodes];

      setSelectedOptions(tags);
      const updatedData = Object.keys(selectedData).reduce((acc, key) => {
        acc[key] = tags.includes(key);
        return acc;
      }, {} as typeof selectedData);
      handleDataChange(updatedData, type, wave);
    },
    [handleDataChange, selectedData, type, wave],
  );

  const handleTagClose = (tag: string) => {
    const key = tag as keyof typeof selectedData;
    if (key in selectedData) {
      selectedData[key] = false;
    }
    setSelectedOptions(selectedOptions.filter((option) => option !== tag));
    handleDataChange(selectedData, type, wave);
  };

  const handleCtrlA = useCallback(
    (event: KeyboardEvent) => {
      if (
        type &&
        (event.ctrlKey || event.metaKey) &&
        event.key === 'a' &&
        menuOpen
      ) {
        event.preventDefault();
        if (
          selectedOptions.length > 0 &&
          preSelected.length === selectedOptions.length
        ) {
          handleSelectedDataChange([]);
        } else {
          handleSelectedDataChange(preSelected);
        }
      }
    },
    [
      type,
      menuOpen,
      selectedOptions.length,
      preSelected,
      handleSelectedDataChange,
    ],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleCtrlA);
    return () => {
      document.removeEventListener('keydown', handleCtrlA);
    };
  }, [selectedOptions, handleDataChange, handleCtrlA]);

  return (
    <>
      <FormControl pb='m'>
        <Menu
          closeOnSelect={false}
          variant='virtual'
          matchWidth
          onClose={() => setMenuOpen(false)}
          onOpen={() => setMenuOpen(true)}
        >
          <FormLabel htmlFor={type}>{label}</FormLabel>
          <MenuButton
            as={Button}
            variant='secondary'
            w='full'
            textAlign='left'
            fontWeight='regular'
            rightIcon={<ChevronDownIcon />}
          >
            Select {wave ? `${wave} tags` : 'general settings'}
          </MenuButton>
          <MenuList maxWidth='unset' zIndex={40}>
            <MenuSearchBox options={data} placeholder='Search...'>
              {(filteredOptions) => (
                <Flex flexDirection='column'>
                  <VirtualizedList
                    rowHeight={35}
                    containerHeight={300}
                    size={filteredOptions.length || data.length}
                  >
                    {(virtualItems) => (
                      <MenuOptionGroup
                        type='checkbox'
                        value={selectedOptions}
                        onChange={(value) => {
                          handleSelectedDataChange(value as string[]);
                        }}
                      >
                        {virtualItems.map(({ index, key, style }) => {
                          if (filteredOptions.length > 0) {
                            const fuseOption = filteredOptions[index];
                            return (
                              <MenuItemOption
                                value={fuseOption.item.value}
                                key={key}
                                style={style}
                              >
                                <MenuFuseHighlight
                                  fallback={fuseOption.item.value}
                                  hit={fuseOption}
                                />
                              </MenuItemOption>
                            );
                          }
                          const { value, displayValue } = data[index];
                          return (
                            <MenuItemOption
                              value={value}
                              key={key}
                              style={style}
                            >
                              {displayValue}
                            </MenuItemOption>
                          );
                        })}
                      </MenuOptionGroup>
                    )}
                  </VirtualizedList>
                </Flex>
              )}
            </MenuSearchBox>
          </MenuList>
        </Menu>
      </FormControl>
      <List spacing='s' mb='m'>
        <ListItem mb='s'>
          {selectedOptions &&
            selectedOptions.map((el, index) => (
              <Tag size='sm' mb='xs' mr='xs' key={index}>
                {el}
                <TagCloseButton>
                  <CloseIcon onClick={() => handleTagClose(el)} />
                </TagCloseButton>
              </Tag>
            ))}
        </ListItem>
      </List>
    </>
  );
};
